import type React from 'react'
import { useState, useEffect, useRef } from 'react'
import Markdown from 'react-markdown'
import type { ContentItem } from '../lib/types'
import { apiClient } from '../lib/api'
import { track } from '../lib/analytics'
import { useToastStore } from '../stores/toastStore'
import { useChatStore } from '../stores/chatStore'
import { useThreadStore } from '../stores/threadStore'
import ConfirmDialog from './ConfirmDialog'

interface Props {
  item: ContentItem
  threadId: number
  entryId: number
}

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; processingText: string }> = {
  youtube: {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    processingText: 'Processing YouTube video...',
  },
  blog: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A8.966 8.966 0 013 12c0-1.264.26-2.466.727-3.559" />
      </svg>
    ),
    processingText: 'Extracting article...',
  },
  pdf: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    processingText: 'Reading PDF...',
  },
  notecard: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
      </svg>
    ),
    processingText: 'Scanning note card...',
  },
  text: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    processingText: 'Reading text file...',
  },
  docx: {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    processingText: 'Reading Word document...',
  },
}

function getTitle(item: ContentItem): string {
  const meta = item.metadata as Record<string, string> | undefined
  if (meta?.title) return meta.title
  if (item.content_type === 'notecard') return 'Note card scan'
  if (item.source_url) return item.source_url
  if (item.file_path) {
    const parts = item.file_path.split('/')
    return parts[parts.length - 1] || 'Uploaded file'
  }
  return 'Content item'
}

function formatTimings(meta: Record<string, unknown> | undefined): React.ReactNode | null {
  if (!meta || typeof meta !== 'object') return null
  const t = (meta.timings_ms as Record<string, unknown> | undefined) || {}
  const parts: string[] = []
  const num = (key: string): number | null => {
    const v = t[key]
    return typeof v === 'number' ? v : null
  }
  const push = (label: string, key: string, threshold = 0) => {
    const v = num(key)
    if (v !== null && v >= threshold) parts.push(`${label} ${(v / 1000).toFixed(1)}s`)
  }
  // Rollups (always shown if present)
  push('up', 'upload')
  // View sub-fields — surface only when slow (>=1s) so happy path stays terse
  push('v', 'view_validate', 1000)
  push('tx', 'view_transform', 1000)
  push('r2w', 'view_r2_write', 1000)
  push('wait', 'queue_wait')
  // Task sub-fields — surface only when slow (>=500ms)
  push('setup', 'task_setup', 500)
  push('r2g', 'r2_get', 500)
  push('dn', 'downscale', 500)
  push('ocr', 'ocr')
  push('post', 'post_ocr', 500)
  push('tot', 'total')
  // Upload size — always shown when known (helps spot oversized gallery uploads)
  const bytes = meta.image_bytes
  if (typeof bytes === 'number' && bytes > 0) {
    parts.push(`${(bytes / (1024 * 1024)).toFixed(1)}MB`)
  }
  if (parts.length === 0) return null
  return parts.map((p, i) => (
    <span key={i}>
      {i > 0 && <span className="mx-0.5">·</span>}
      {p}
    </span>
  ))
}

function getThumbnail(item: ContentItem): string | null {
  const meta = item.metadata as Record<string, string> | undefined
  if (item.content_type === 'youtube') {
    if (meta?.thumbnail) return meta.thumbnail
    if (meta?.video_id) return `https://img.youtube.com/vi/${meta.video_id}/hqdefault.jpg`
  }
  if (meta?.thumbnail_url) return meta.thumbnail_url
  if (item.content_type === 'notecard' && item.image_url) return item.image_url
  return null
}

interface TranscriptSegment {
  text: string
  start: number
}

function formatTimecode(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Group segments into ~30-second blocks, snapping to sentence boundaries for readability */
function groupSegments(segments: TranscriptSegment[]): { start: number; text: string }[] {
  const groups: { start: number; text: string }[] = []
  let currentGroup: TranscriptSegment[] = []
  let groupStart = 0
  const sentenceEndRegex = /[.?!]\s*$/

  for (const seg of segments) {
    if (currentGroup.length === 0) {
      groupStart = seg.start
    }
    currentGroup.push(seg)
    const elapsed = seg.start - groupStart
    if (elapsed >= 30 && currentGroup.length > 0) {
      // Past the 30s target — break if we hit a sentence boundary or exceed 45s
      if (sentenceEndRegex.test(seg.text) || elapsed >= 45) {
        groups.push({ start: groupStart, text: currentGroup.map(s => s.text).join(' ') })
        currentGroup = []
      }
    }
  }
  if (currentGroup.length > 0) {
    groups.push({ start: groupStart, text: currentGroup.map(s => s.text).join(' ') })
  }
  return groups
}

/** Chevron icon that rotates based on expanded state */
function Chevron({ expanded, className = '' }: { expanded: boolean; className?: string }) {
  return (
    <svg
      className={`w-4 h-4 text-text-muted transition-transform duration-200 ${expanded ? 'rotate-180' : ''} ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

export default function ContentItemCard({ item, threadId, entryId }: Props) {
  const [expanded, setExpanded] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const config = TYPE_CONFIG[item.content_type] || TYPE_CONFIG.blog
  const status = item.processing_status

  const [timedOut, setTimedOut] = useState(false)
  const prevStatusRef = useRef(status)

  // Refresh thread list when ingestion completes (picks up auto-generated title)
  useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = status
    if ((prev === 'pending' || prev === 'processing') && status === 'completed') {
      useThreadStore.getState().fetchThreads()
    }
  }, [status])

  // Poll for completion when processing. PDF and notecard vision OCR can take up to 15 min.
  const pollingTimeoutMs = (item.content_type === 'pdf' || item.content_type === 'notecard')
    ? 15 * 60 * 1000
    : 5 * 60 * 1000
  useEffect(() => {
    if (status !== 'pending' && status !== 'processing') return
    setTimedOut(false)
    const interval = setInterval(() => {
      useChatStore.getState().fetchTimeline(threadId)
    }, 3000)
    const timeout = setTimeout(() => {
      clearInterval(interval)
      setTimedOut(true)
    }, pollingTimeoutMs)
    return () => { clearInterval(interval); clearTimeout(timeout) }
  }, [status, threadId, pollingTimeoutMs])

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setRemoving(true)
    track('content_item_deleted', { thread_id: threadId, content_type: item.content_type, status })
    try {
      await useChatStore.getState().removeEntry(threadId, entryId)
    } catch {
      setRemoving(false)
    }
  }

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setRetrying(true)
    const meta = item.metadata as Record<string, unknown> | undefined
    track('content_import_retried', { thread_id: threadId, content_type: item.content_type, attempts: meta?.attempts })
    try {
      await apiClient.post(`research/threads/${threadId}/content/${entryId}/retry/`)
      useChatStore.getState().fetchTimeline(threadId)
    } catch {
      useToastStore.getState().addToast('Retry failed. Please try again.', 'error')
    } finally {
      setRetrying(false)
    }
  }

  // Fire once when user sees the failed state
  const errorTrackedRef = useRef(false)
  useEffect(() => {
    if ((status === 'failed' || timedOut) && !errorTrackedRef.current) {
      errorTrackedRef.current = true
      const meta = item.metadata as Record<string, unknown> | undefined
      track('content_error_shown', {
        thread_id: threadId,
        content_type: item.content_type,
        timed_out: timedOut,
        attempts: meta?.attempts,
      })
    }
  }, [status, timedOut, threadId, item.content_type, item.metadata])

  // Pending / Processing state
  if ((status === 'pending' || status === 'processing') && !timedOut) {
    const processingImage = item.content_type === 'notecard' && item.image_url
    return (
      <div className="bg-surface rounded-xl overflow-hidden">
        {processingImage && (
          <div className="relative">
            <img
              src={item.image_url!}
              alt="Note card"
              className="w-full max-h-48 object-contain bg-bg opacity-70"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-8 h-8 border-3 border-accent border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 px-4 py-3 animate-pulse">
          <div className="text-text-muted shrink-0">{config.icon}</div>
          <div className="flex-1 min-w-0">
            {!processingImage && <div className="h-3 bg-elevated rounded w-3/4 mb-2" />}
            <p className="text-sm text-text-muted">{config.processingText}</p>
          </div>
          {!processingImage && (
            <span className="w-4 h-4 border-2 border-accent border-t-transparent rounded-full animate-spin shrink-0" />
          )}
        </div>
      </div>
    )
  }

  // Timed out or failed state
  if (status === 'failed' || timedOut) {
    const meta = item.metadata as Record<string, unknown> | undefined
    const rawError = !timedOut && meta?.error as string | false
    const attempts = meta?.attempts as number | undefined
    const errorMsg = rawError === 'No transcript available' && item.content_type === 'youtube'
      ? 'No captions found. For live streams, YouTube may take a few hours to generate them — try again later.'
      : rawError || undefined
    return (
      <div className="relative bg-surface rounded-xl px-4 py-3 border border-error/30">
        <button
          onClick={handleRemove}
          disabled={removing || retrying}
          aria-label="Remove"
          className="absolute top-2 right-2 p-1 text-text-muted hover:text-error transition-colors disabled:opacity-50"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="flex items-center gap-3 pr-6">
          <div className="text-error shrink-0">{config.icon}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-error">
              Failed to process{attempts ? ` (${attempts} attempts)` : ''}
            </p>
            <p className="text-xs text-text-muted truncate">{getTitle(item)}</p>
            {errorMsg && (
              <p className="text-xs text-text-muted/60 mt-0.5 break-words whitespace-normal">{errorMsg}</p>
            )}
          </div>
          <button
            onClick={handleRetry}
            disabled={retrying || removing}
            className="text-xs font-medium text-accent hover:text-accent/80 px-3 py-1.5 bg-elevated rounded-lg transition-colors disabled:opacity-50 shrink-0"
          >
            {retrying ? 'Retrying...' : 'Retry'}
          </button>
        </div>
      </div>
    )
  }

  // Completed state
  const title = getTitle(item)
  const thumbnail = getThumbnail(item)
  const summaryText = item.summary || item.processed_content
  const hasExpandableContent = !!(summaryText || (item.content_type === 'notecard' && item.ocr_text))
  const hasTranscript = !!(item.raw_content && item.raw_content !== summaryText)
  const isYouTube = item.content_type === 'youtube'
  const isNotecard = item.content_type === 'notecard'

  // Get timestamped segments from metadata if available
  const meta = item.metadata as Record<string, unknown> | undefined
  const segments = (meta?.transcript_segments ?? []) as TranscriptSegment[]
  const hasTimecodes = segments.length > 0
  const videoId = meta?.video_id as string | undefined

  const handleCardClick = () => {
    if (!hasExpandableContent) return
    if (!expanded) {
      track('content_card_expanded', { thread_id: threadId, content_type: item.content_type })
    } else {
      setShowTranscript(false)
    }
    setExpanded(!expanded)
  }

  const handleTranscriptToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowTranscript(!showTranscript)
  }

  return (
    <div
      className="bg-surface rounded-xl overflow-hidden"
    >
      {/* YouTube thumbnail with play button */}
      {isYouTube && thumbnail && (
        <a
          href={item.source_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="block relative"
        >
          <img
            src={thumbnail}
            alt={title}
            className="w-full aspect-video object-cover"
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center">
              <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </a>
      )}

      {/* Notecard image preview */}
      {isNotecard && thumbnail && (
        <img
          src={thumbnail}
          alt="Note card"
          className="w-full max-h-64 object-contain bg-bg"
        />
      )}

      <div className="px-4 py-3">
        {/* Header row: icon/thumbnail + title + chevron — only this row toggles expand */}
        <div
          className={`flex items-start gap-3 ${hasExpandableContent ? 'cursor-pointer active:opacity-75 transition-opacity' : ''}`}
          onClick={handleCardClick}
        >
          {!isYouTube && !isNotecard && thumbnail ? (
            <img
              src={thumbnail}
              alt=""
              className="w-12 h-12 rounded-lg object-cover shrink-0"
            />
          ) : !isYouTube && !isNotecard ? (
            <div className="text-text-muted shrink-0 pt-0.5">{config.icon}</div>
          ) : !thumbnail ? (
            <div className="text-text-muted shrink-0 pt-0.5">{config.icon}</div>
          ) : null}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{title}</p>
            {item.source_url && (
              <a
                href={item.source_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-accent hover:underline truncate block"
              >
                {(() => { try { return new URL(item.source_url).hostname } catch { return item.source_url } })()}
              </a>
            )}
            {(() => {
              const timings = formatTimings(meta)
              return timings ? (
                <p className="text-xs text-text-secondary font-mono mt-1 break-words">{timings}</p>
              ) : null
            })()}
          </div>
          <div className="flex items-center gap-1 shrink-0 pt-0.5 select-none">
            {hasExpandableContent && <Chevron expanded={expanded} />}
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(true) }}
              disabled={removing}
              aria-label="Remove content"
              className="p-1 text-text-muted/40 hover:text-error transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          </div>
        </div>

        {/* Collapsed preview — tapping also expands */}
        {hasExpandableContent && !expanded && (
          <div className="relative mt-2 cursor-pointer" onClick={handleCardClick}>
            <p className="text-sm text-text-secondary line-clamp-3">
              {isNotecard && item.ocr_text ? item.ocr_text : summaryText}
            </p>
            <div className="absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-surface to-transparent pointer-events-none" />
          </div>
        )}

        {/* Expanded content */}
        {expanded && (
          <div className="mt-2">
            {/* Notecard OCR text */}
            {isNotecard && item.ocr_text && (
              <p className="text-sm text-text-secondary">
                {item.ocr_text}
              </p>
            )}

            {/* Summary */}
            {summaryText && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-text-muted mb-1">Summary</p>
                <div className="prose prose-sm prose-invert max-w-none text-text-secondary [&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_li]:my-0.5 [&_a]:text-accent [&_strong]:text-text-primary [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm">
                  <Markdown>{summaryText}</Markdown>
                </div>
              </div>
            )}

            {/* Transcript toggle */}
            {hasTranscript && (
              <div className="mt-3 border-t border-elevated pt-3">
                <button
                  onClick={handleTranscriptToggle}
                  className="flex items-center gap-1.5 text-xs font-medium text-text-muted hover:text-text-secondary transition-colors w-full"
                >
                  <Chevron expanded={showTranscript} className="w-3 h-3" />
                  <span>{showTranscript
                    ? `Hide ${isYouTube ? 'transcript' : 'article'}`
                    : `View full ${isYouTube ? 'transcript' : 'article'}`
                  }</span>
                </button>

                {showTranscript && (
                  <div className="mt-3 max-h-96 overflow-y-auto overscroll-contain space-y-3">
                    {hasTimecodes ? (
                      // Timestamped transcript with clickable timecodes
                      groupSegments(segments).map((group, i) => (
                        <div key={i} className="flex gap-2">
                          <a
                            href={`https://www.youtube.com/watch?v=${videoId}&t=${Math.floor(group.start)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-accent hover:underline font-mono shrink-0 pt-0.5 w-12 text-right"
                          >
                            {formatTimecode(group.start)}
                          </a>
                          <p className="text-sm text-text-secondary/80">{group.text}</p>
                        </div>
                      ))
                    ) : (
                      // Plain transcript without timecodes
                      <p className="text-sm text-text-secondary/80 whitespace-pre-line">
                        {item.raw_content}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmDelete}
        title="Remove content"
        message={`Remove "${title}" from this thread? This cannot be undone.`}
        confirmLabel="Remove"
        destructive
        onConfirm={() => { setConfirmDelete(false); handleRemove({ stopPropagation: () => {} } as React.MouseEvent) }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  )
}
